/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import { CheckThinIcon } from "@smartsheet/lodestar-icons";
import { genId } from "@smartsheet/lodestar-utils";
import { LogActionId } from "@smartsheet/logging-state-service";
import { themed } from "@smartsheet/sds-theme-internal";
import { CheckmarkIcon } from "@smartsheet/ursa-icons";
import classNames from "classnames";
import fuzzySort from "fuzzysort";
import { Ref, useCallback, useMemo } from "react";
import { I18nContextProvider } from "../../i18n/i18n";
import { useTheme } from "../../theme/utils/useTheme";
import { useLoggingDecorator, useLoggingEnabledByDefault } from "../../util/logging";
import { TypeRampV2 } from "../typography";
import { SelectBasic } from "./components/select/SelectBasic";
import { SelectComboBox } from "./components/select/SelectComboBox";
import { optionFontTheme } from "./components/selectV2Theme";
import { FuzzySearchResult } from "./components/shared/FuzzySearchResult";
import { OptionBase } from "./components/shared/OptionBase";
import { FUZZYSORT_MIN_THRESHOLD, DEFAULT_MAX_MENU_HEIGHT } from "./constants";
import { injectCatalog } from "./i18n/SelectCatalog";
import { BaseOptionType, SelectProps } from "./types";
import { internalOptionToString } from "./utils";

export const selectedItemTheme = themed({
    icon: {
        "lodestar-light": <CheckThinIcon size="small" color={color("color--icons--primary--default")} />,
        "ursa-light": <CheckmarkIcon size={20} color={color("color--icons--primary--strong")} />,
    },
});

export const Select = <Option extends BaseOptionType>({
    onChange,
    clientId,
    formatOption,
    innerRef,
    isOptionDisabled,
    isLoading = false,
    isSearchable = false,
    isReadonly = false,
    isClearable = false,
    useFuzzySearch = false,
    locale,
    maxMenuHeight = DEFAULT_MAX_MENU_HEIGHT,
    maxOptionsUntilVirtualization = 50,
    menuId: providedMenuId,
    onCreateOption,
    optionHeight,
    optionToString = internalOptionToString,
    filterOption: providedFilterOption,
    sortFilteredOptions,
    inputValue,
    setInputValue,
    hideDropdownArrow = false,
    shouldDisableClearingOnInputClicks = false,
    overrides,
    highlightFirstOptionAutomatically,
    ...rest
}: SelectProps<Option, HTMLButtonElement | HTMLInputElement>) => {
    injectCatalog();
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    if (process.env.NODE_ENV !== "production") {
        const hiddenInputNameDefined = typeof rest.hiddenInputName === "string";
        const getHiddenInputValueDefined = typeof rest.getHiddenInputValue === "function";
        if (hiddenInputNameDefined !== getHiddenInputValueDefined) {
            console.warn(
                "MultiSelect: To use the hidden input, you must include both" +
                    " props: getHiddenInputValue and hiddenInputName"
            );
        }
    }

    const isCreatable = typeof onCreateOption === "function";
    const isComboBox = isSearchable || isCreatable;

    const defaultFilterOption = useCallback(
        (opt: Option, input: string, fuzzySearchScoreMap?: Map<Option, number>) => {
            if (useFuzzySearch) {
                const result = fuzzySort.single(input, optionToString(opt));
                if (result) {
                    if (fuzzySearchScoreMap) {
                        fuzzySearchScoreMap.set(opt, result.score);
                    }
                    return result.score >= FUZZYSORT_MIN_THRESHOLD;
                }
                return false;
            } else {
                return optionToString(opt)?.toLowerCase().startsWith(input);
            }
        },
        [optionToString, useFuzzySearch]
    );

    const onChangeWithLogging = useLoggingEnabledByDefault(
        useLoggingDecorator({
            controlType: "select",
            clientId,
        }),
        { actionId: LogActionId.CLICK },
        onChange
    );

    const defaultFormatOption: Required<SelectProps<Option, never>>["formatOption"] = (
        option,
        index,
        selectedIndex,
        optionSpreadProps,
        isOptionSelected,
        metadata
    ) => {
        let optionLabel: string | JSX.Element = optionToString(option);

        if (useFuzzySearch) {
            optionLabel = (
                <FuzzySearchResult inputValue={metadata.inputValue} option={option} optionToString={optionToString} />
            );
        }
        return (
            <OptionBase
                className={classNames("sds-option-base", {
                    "highlight-first-option-auto": highlightFirstOptionAutomatically,
                })}
                height={optionHeight}
                aria-disabled={metadata.isDisabled}
                {...optionSpreadProps}
            >
                {/* @ts-expect-error variant is complaining */}
                <TypeRampV2 lineClamp={1} variant={optionFontTheme.variant(themeMode)} style={{ color: "inherit" }}>
                    {optionLabel}
                </TypeRampV2>
                {isOptionSelected && (
                    <span style={{ marginLeft: "auto", paddingLeft: 8 }}>{selectedItemTheme.icon(themeMode)}</span>
                )}
            </OptionBase>
        );
    };

    const menuId = useMemo(() => providedMenuId || genId("select-menu"), [providedMenuId]);

    const sharedProps = {
        onChange: onChangeWithLogging,
        clientId,
        formatOption: formatOption || defaultFormatOption,
        isOptionDisabled: isOptionDisabled,
        isLoading,
        isReadonly,
        isClearable,
        locale,
        maxMenuHeight,
        maxOptionsUntilVirtualization,
        menuId,
        optionToString,
        optionHeight,
        filterOption: providedFilterOption || defaultFilterOption,
        hideDropdownArrow,
        overrides,
    };

    return (
        <I18nContextProvider locale={locale}>
            {isComboBox ? (
                <SelectComboBox
                    {...rest}
                    {...sharedProps}
                    highlightFirstOptionAutomatically={highlightFirstOptionAutomatically}
                    onCreateOption={onCreateOption}
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    innerRef={innerRef as Ref<HTMLInputElement>}
                    shouldDisableClearingOnInputClicks={shouldDisableClearingOnInputClicks}
                    sortFilteredOptions={sortFilteredOptions}
                />
            ) : (
                <SelectBasic
                    {...rest}
                    {...sharedProps}
                    innerRef={innerRef as Ref<HTMLButtonElement>}
                    shouldDisableClearingOnInputClicks={shouldDisableClearingOnInputClicks}
                />
            )}
        </I18nContextProvider>
    );
};

Select.displayName = "Select";
